import { signInWithEmailAndPassword } from 'firebase/auth';
import auth from '../../firebase';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styles from './Auth.module.css';
import Password from './Password';
import SubmitButton from './SubmitButton';
import Spinner from '../../components/Spinner';

const SignUpPage = () => {
  const [samePassword, setSamePassword] = useState(true);
  const [emailExists, setEmailExists] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    repeatPassword: '',
    checkbox: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const [validToken, setValidToken] = useState();

  useEffect(() => {
    //TODO: If user is logged in, and clicks on accept invitation to an invite rather than going to login page, they should automatically be redirected to /profile

    const verifyToken = async () => {
      setLoading(true);
      const url = process.env.REACT_APP_FLASK_WEBSERVER + `verifyToken?token=${token}`;

      const res = await fetch(url);
      const data = await res.json();
      if (res.status === 200) {
        setValidToken(true);
        setFormData({
          ...formData,
          email: data['email'],
          vertical_joined: data['vertical_id'],
          firstName: data['name'] ? data['name'].split(' ')[0] : '',
          lastName:
            data['name'] && data['name'].split(' ').length > 1
              ? data['name'].split(' ')[data['name'].split(' ').length - 1]
              : '',
          vertical_name: data['vertical_name'],
        });
      } else {
        setValidToken(false);
      }
      setLoading(false);
    };
    if (token) verifyToken();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const url = token
      ? process.env.REACT_APP_FLASK_WEBSERVER + `createUser?token=${token}`
      : process.env.REACT_APP_FLASK_WEBSERVER + `createUserWithoutInvitation`;

    try {
      // Create user
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData }),
      });

      if (res.ok) {
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        navigate('/select-role');
      } else {
        setEmailExists(true);
      }
    } catch (error) {}
    setSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    validateForm({ ...formData, [name]: value });
  };

  const validateForm = (values) => {
    const { firstName, lastName, password, repeatPassword } = values;
    const isValid =
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      password.length >= 8 &&
      password === repeatPassword;
    setIsFormValid(isValid);

    if (repeatPassword.length > 0 && password !== repeatPassword) {
      setSamePassword(false);
    } else {
      setSamePassword(true);
    }
  };

  return loading === false ? (
    <div style={{ width: '320px' }}>
      <h2>Signup</h2>

      <form onSubmit={onSubmit}>
        <p className={styles['input-label']}>Sign up with:</p>
        <div>
          <input
            className={`${styles['input-email']}`}
            style={{ color: validToken ? '#717782' : '' }}
            type="email"
            name="email"
            autoComplete="off"
            required
            value={formData.email}
            placeholder="Your email"
            disabled={validToken ? true : false}
            onChange={handleChange}
          />
        </div>

        <p className={styles['input-label']}>First name</p>
        <div>
          <input
            className={`${styles['input-text']}`}
            type="text"
            name="firstName"
            value={formData.firstName}
            placeholder="Your first name"
            autoComplete="off"
            required
            onChange={handleChange}
          />
        </div>

        <p className={styles['input-label']}>Last name</p>
        <div>
          <input
            className={`${styles['input-text']}`}
            type="text"
            name="lastName"
            value={formData.lastName}
            placeholder="Your last name"
            autoComplete="off"
            required
            onChange={handleChange}
          />
        </div>

        <Password
          name="password"
          password={formData.password}
          onChange={handleChange}
          pattern={true}
        />
        <p className={styles.roleDescription}>
          At least 8 characters, 1 capital, 1 symbol, 1 number.
        </p>

        <Password
          label={'Repeat Password'}
          name="repeatPassword"
          password={formData.repeatPassword}
          onChange={handleChange}
          error={!samePassword}
        />
        {!samePassword && <p className={styles.error}>The password doesn’t match.</p>}

        <div
          style={{
            paddingTop: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <input
            type="checkbox"
            name="checkbox"
            className={styles['checkbox']}
            onChange={() => setIsChecked((prevValue) => !prevValue)}
          />
          <label className={styles['checkbox-label']}>
            I agree with The Simple platform’s{' '}
            <a href="https://www.simple-platform.com/terms-and-conditions" target="_blank">
              Terms and Conditions
            </a>
            &nbsp;and&nbsp;
            <a href="https://www.simple-platform.com/privacy-policy" target="_blank">
              Privacy Policy
            </a>
          </label>
        </div>

        <div style={{ paddingTop: '20px' }}>
          <SubmitButton
            text={'Continue'}
            disabled={submitting || !isFormValid || !isChecked || emailExists}
            disabledText={!isFormValid || !isChecked || emailExists}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            fontSize: '14px',
          }}
        >
          {emailExists ? (
            <p className={styles.error}>
              Email already in use. Try <Link to={'/'}>logging in</Link> instead
            </p>
          ) : (
            <>
              <p>Already have an account?</p>
              <Link to="/login">Sign In</Link>
            </>
          )}
        </div>
      </form>
    </div>
  ) : (
    <Spinner />
  );
};

export default SignUpPage;
